import * as dte from "../../DtEventTypes";
import { EventDispatcher } from "../../../application/EventDispatcher";
import { FocusedTime } from "./FocusedTime";
import { DtConstants } from "../../schema/DtConstants";
import { StatusIntervalTree } from "./StatusIntervalTree";
import { TaskDroppedError, TaskQueue } from "./TaskQueue";
import { dayjs, nowSec } from "./time";
import { TimeInterval } from "./TimeInterval";
import { TimelinePlayer } from "./TimelinePlayer";

const { Intervals, Now } = DtConstants.Timeline;
/** @typedef {number|"dynamic_now"} MaybeNowTS Since JSDoc types are static, make sure "Now" matches DtConstants. */

const DATE_FORMAT = 'YYYY-MM-DD';

const MIN = 60;
const DAY = 24 * 60 * MIN;

// Central telemetry ticker.
const TICK_MS = 60 * 1000;


/** Provides a restricted temporal view on telemetry data. */
export class TelemetryView extends EventDispatcher {
  /** Time interval (maybe symbolic). */
  interval;
  /** Point in time (maybe symbolic). */
  time;

  /** Cancellation ID for the ticker interval. */
  #intervalID;
  #streamMgr;
  #taskQueue;
  /** Cached stream thresholds */
  #thresholds;
  /** Cached stream offline timeouts */
  #offlineTimeouts;
  /** Playback controls */
  player;

  constructor(streamMgr) {
    super();

    this.#streamMgr = streamMgr;
    this.#taskQueue = new TaskQueue();

    this.boundOnStreamsInfoChanged = this.#onStreamsInfoChanged.bind(this);
  }

  dispose() {
    this.#streamMgr.facility.eventTarget.removeEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnStreamsInfoChanged);
    this.clearListeners();
    this.#taskQueue.dispose();
    this.player?.dispose();
  }

  init(viewer, eventSource) {
    this.player = new TimelinePlayer(this, viewer, eventSource);
    this.time = new FocusedTime(Now);
    this.interval = TimeInterval.Create(Intervals.LAST_3_DAYS);

    const facility = this.#streamMgr.facility;
    facility.eventTarget.addEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnStreamsInfoChanged);

    const model = facility.getDefaultModel();
    if (model) {
      this.#setDefaultState();
    }

    //this.debugEvents(true);
    //this.#streamMgr.facility.eventTarget.debugEvents(true);
  }

  /**
   * Get readings that applies for a specific instant in time, or nothing if it has no data.
   *
   * Will return the first reading for a period that matches up to offline age ago.
   */
  async getInstantReadings(filter) {
    const defaultModel = this.#streamMgr.facility.getDefaultModel();
    if (!defaultModel) return;

    return defaultModel.asyncPropertyOperation({
      operation: "GET_INSTANT_READINGS",
      filter,
      symbolicTime: this.time.focus
    });
  }

  /**
   * Get an interval tree of periods in time when streams where out of compliance.
   * @return {Promise<*>}
   */
  async getOutOfComplianceRegions() {
    const defaultModel = this.#streamMgr.facility.getDefaultModel();
    if (!defaultModel) return;

    return defaultModel.asyncPropertyOperation({ operation: "GET_TIMELINE_OOC_REGIONS" }).
    then((serialized) => StatusIntervalTree.rehydrate(serialized));
  }

  /**
   * Get per attribute value range (i.e. min max)
   * @return {Promise.<{ min, max }|undefined>}
   */
  async getDataRange(attrId) {
    const defaultModel = this.#streamMgr.facility.getDefaultModel();
    if (!defaultModel) return;

    return defaultModel.asyncPropertyOperation({ operation: "GET_TIMELINE_VALUE_RANGE", attrId });
  }

  handleInstantReadingsChanged({ action, instant, updated }) {
    if (action !== 'replace') {
      console.info('unimplemented action');
      return; // optim - if needed, just send a diff to apply.
    }

    // We don't need to update time nor send it to the user, as the user should re-request it from the view.
    this.dispatchEvent({ type: dte.DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT, instant, updated });

    if (this.time.isLive()) {
      this.#switchIntervalLiveIfReachedEnd();
    }
  }

  handleOocRegionsChanged({ action, regionsTree }) {
    if (action !== 'replace') {
      console.info('unimplemented action');
      return; // optim - if needed, just send a diff to apply.
    }

    // Regenerate instance based on JS object.
    const regions = StatusIntervalTree.rehydrate(regionsTree);
    this.dispatchEvent({ type: dte.DT_TELEMETRY_REGIONS_EVENT, regionsTree: regions });
  }

  /** Set the timeline's time as a MaybeNowTS */
  setTime(time, ignorePlayMode = false, ts = Date.now()) {
    if (time === this.time.focus) return;

    if (!ignorePlayMode && this.player.isPlaying) {
      console.warn('The time cannot be updated while the timeline is in the play mode');
      return;
    }

    this.time.focus = time;
    this.time.clamp(this.interval);
    this.dispatchEvent({ type: dte.DT_TELEMETRY_CHANGED_EVENT, time: this.time, askedAt: ts });

    // This call forces instant readings refresh and sends a notification to other consumers
    this.getInstantReadings({ dbIds: [] });

    return ts;
  }

  /**
   * Sets a time interval.
   * @param {string} key One of the named intervals key
   * @param {any} [opt] Optional configuration
   * @param {number} [ts] Optional timestamp of the request.
   * @return {number}
   */
  setInterval(key, opt, ts = Date.now()) {
    if (this.player.isPlaying) {
      console.warn('The interval cannot be updated while the timeline is in the play mode');
      return;
    }
    // Abusing the set view state with an undefined time.
    return this.setViewState(undefined, key, opt, ts);
  }

  /**
   * Sets both the time and interval of the timeline. The more specific version (setTime/Interval) should be favoured.
   * @param {MaybeNowTS} time Timeline's time
   * @param {string} key One of the named intervals key
   * @param {any} [opt] Optional configuration
   * @param {number} [ts] Optional timestamp of the request.
   * @return {number}
   */
  setViewState(time, key, opt, ts = Date.now()) {
    if (this.player.isPlaying) {
      console.warn('The telemetry view state cannot be updated while the timeline is in the play mode');
      return;
    }

    const interval = TimeInterval.Create(key, opt);

    // First checks if this request is a no-op.
    if (interval.equals(this.interval)) {
      // The time of application is "undefined" if nothing changed.
      return time ? this.setTime(time, false, ts) : undefined;
    }

    this.#makeRequestForData(time, interval, ts);

    return ts;
  }

  async #setTimelineData(interval, thresholds, timeouts) {
    const [utcBeg, utcEnd] = interval.asUTCDateRange();

    const defaultModel = this.#streamMgr.facility.getDefaultModel();
    if (!defaultModel) return Promise.resolve();

    // Waits for the property DB to be present.
    await defaultModel.waitForLoad(false, true);

    return defaultModel.asyncPropertyOperation({
      operation: "SET_TIMELINE_DATA",
      iotWorkerCbId: this.#streamMgr.iotWorkerCbId,
      // Add allowance for at least a 1 day lookBack.
      range: [utcBeg.subtract(1, 'day').format(DATE_FORMAT), utcEnd.format(DATE_FORMAT)],
      thresholds: thresholds,
      offlineTimeouts: timeouts,
      duration: interval.duration.asMilliseconds(),
      symbolicEnd: interval.end,
      symbolicTime: this.time.focus
    });
  }

  #makeRequestForData(time, interval, askedAt) {
    const request = async (_) => {
      const [thresholds, timeouts] = await this.#streamMgr.getAllStreamInfos().then(toStreamInfoMap);
      // Note: Data may contain more than the exact time period covered by the interval.
      await this.#setTimelineData(interval, thresholds, timeouts);

      this.#thresholds = thresholds;
      this.#offlineTimeouts = timeouts;
      this.interval = interval;

      // Allows for time here to be either a FocusedTime or a timestamp.
      if (time) this.time.focus = time.focus ?? time;
      const timeClamped = this.time.clamp(interval);
      const timeDetails = (time || timeClamped) && { time: this.time };

      this.dispatchEvent({ type: dte.DT_TELEMETRY_CHANGED_EVENT, view: this, ...timeDetails, askedAt });
    };

    return this.#taskQueue.enqueue(request).
    catch((err) => {
      if (err instanceof TaskDroppedError) return;
      console.error(err);
    });
  }

  /**
   * Returns an instantiated time and interval, as DayJS datetimes.
   * @param {string} [tzId] TZ Database timezone identifier. Defaults to user's
   * @param {number} [now] Optional override on what is now, as a UNIX timestamp.
   */
  resolve(tzId, now = Date.now()) {
    const res = this.interval.resolve(tzId, now);
    res.time = this.time.resolve(tzId, now);

    return res;
  }

  // Keeping internal utility methods lower
  addEventListener(type, listener, options) {
    super.addEventListener(type, listener, options);
    this.#updateTicker();
  }

  clearListeners() {
    super.clearListeners();
    this.#updateTicker();
  }

  removeEventListener(type, listener) {
    super.removeEventListener(type, listener);
    this.#updateTicker();
  }

  /** Sets the range to be either the last 3 days, or the last 3 days of readings. */
  async #setDefaultState() {
    const latest = await this.#streamMgr.getLatestReading();

    // Defaults timeline to show where the data is.
    if (latest && nowSec() - latest >= 3 * DAY) {
      this.interval = TimeInterval.Create(Intervals.CUSTOM, {
        duration: dayjs.duration({ days: 3 }),
        end: latest * 1000 + 1000 // Since the interval's end is exclusive, we add 1 second to include the last reading
      });
      this.time = new FocusedTime(latest * 1000);
    }

    return this.#makeRequestForData(this.time, this.interval, Date.now());
  }

  #onStreamsInfoChanged = () => {
    // No need to update if we didn't receive base data yet.
    if (this.#offlineTimeouts) {
      return this.#makeRequestForData(this.time, this.interval, Date.now());
    }
  };

  // If the time is live, but the interval is static, the interval becomes live if time ticks until the end.
  #switchIntervalLiveIfReachedEnd() {
    // this assumes the time is live.
    if (this.time.isAfter(this.interval)) {
      this.interval.end = Now;
      // Inform the worker of the change in configuration.
      this.#makeRequestForData(this.time, this.interval, Date.now());
    }
  }

  // Use to indicate a common "clock time changed", for any UI that might need it.
  #tick = () => {
    // Shortcut to prevent time based IOT requests while the player is playing.
    if (this.player.isPlaying) return;

    if (this.time.isLive()) {
      // Interval is live, or fixed, but since it is a time passed event, it can only be moving backwards.
      this.#switchIntervalLiveIfReachedEnd();
    } else {
      // Ensures time is dragged by the window's range, if the end of the range is live.
      this.time.clamp(this.interval);
    }

    this.dispatchEvent({ type: dte.DT_TELEMETRY_TICK_EVENT, ts: Date.now() });
  };

  #updateTicker() {
    if (!this.time) return; // Timeline has not yet been initialized, no need for ticker.

    const hasSubscribers = this.listeners?.[dte.DT_TELEMETRY_TICK_EVENT]?.length > 0;

    if (!hasSubscribers && this.#intervalID) {
      clearInterval(this.#intervalID);
      this.#intervalID = null;
      return;
    }
    if (hasSubscribers && !this.#intervalID) {
      this.#intervalID = setInterval(this.#tick, TICK_MS);
    }
  }
}

function toStreamInfoMap(streamInfos) {
  const res = {};
  const timeouts = {};
  for (const stream of streamInfos) {
    timeouts[stream.fullId] = stream.settings.offlineTimeout;
    for (const attr of stream.streamAttrs) {
      const threshold = attr.allowedValues?.thresholds;
      if (!threshold) continue;

      const perConn = res[stream.fullId] ?? (res[stream.fullId] = {});
      perConn[attr.id] = threshold;
    }
  }
  return [res, timeouts];
}